/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-unused-expressions */
import React, { useState, useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';

import Transition from '../Transition';
import SearchResults from './SearchResults';
import { useServicerStore } from '../../store';

import SearchService from '../../services/SearchService';

let timer;

function ModalSearch({ id, searchId, modalOpen, setModalOpen }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const userRole = useServicerStore((state) => state.userRole);
  const isLender = userRole === 'lender';

  const modalContent = useRef(null);
  const searchInput = useRef(null);

  const { data: searchResults, isFetching } = useQuery({
    queryKey: ['search', debouncedSearchTerm, isLender],
    queryFn: () => SearchService.search({ query: searchTerm, isLender }),
    enabled: !!debouncedSearchTerm,
    keepPreviousData: true,
  });

  const hasResults =
    searchResults &&
    Object.values(searchResults).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!modalOpen || modalContent.current.contains(target)) return;
      setModalOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!modalOpen || keyCode !== 27) return;
      setModalOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    modalOpen && searchInput.current.focus();
  }, [modalOpen]);

  // Clear input on modal close
  useEffect(() => {
    if (!modalOpen) {
      setTimeout(() => {
        setSearchTerm('');
      }, 200);
    }
  }, [modalOpen]);

  // Debounce search term
  useEffect(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
  }, [searchTerm]);

  return (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed inset-0 z-[1000] overflow-hidden flex items-start top-20 mb-4 justify-center px-4 sm:px-6"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
          ref={modalContent}
          className="bg-white dark:bg-slate-800 border border-transparent dark:border-slate-700 overflow-auto max-w-2xl w-full max-h-full rounded shadow-lg"
        >
          {/* Search form */}
          <form
            className="border-b border-slate-200 dark:border-slate-700"
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <div className="relative">
              <label htmlFor={searchId} className="sr-only">
                Search
              </label>
              <input
                id={searchId}
                className="w-full dark:text-slate-300 bg-white dark:bg-slate-800 border-0 focus:ring-transparent placeholder-slate-400 dark:placeholder-slate-500 appearance-none py-3 pl-10 pr-4"
                type="search"
                placeholder="Search Anything…"
                ref={searchInput}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <button
                className="absolute inset-0 right-auto group z-[1000]"
                type="submit"
                aria-label="Search"
              >
                <svg
                  className="w-4 h-4 shrink-0 fill-current text-slate-400 dark:text-slate-500 group-hover:text-slate-500 dark:group-hover:text-slate-400 ml-4 mr-2"
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M7 14c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7zM7 2C4.243 2 2 4.243 2 7s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z" />
                  <path d="M15.707 14.293L13.314 11.9a8.019 8.019 0 01-1.414 1.414l2.393 2.393a.997.997 0 001.414 0 .999.999 0 000-1.414z" />
                </svg>
              </button>
            </div>
          </form>
          {hasResults ? (
            <div className="py-4 px-2">
              <SearchResults
                setModalOpen={setModalOpen}
                searchResults={searchResults || {}}
              />
            </div>
          ) : (
            <div
              className={`z-[1000] relative text-center text-slate-400 dark:text-slate-500 p-4 ${
                isFetching || !debouncedSearchTerm ? 'hidden' : ''
              }`}
              aria-live="polite"
            >
              No results found
            </div>
          )}
        </div>
      </Transition>
    </>
  );
}

export default ModalSearch;
